import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {AdapterMoment, Alert, Box, ButtonBase, DateCalendar, LocalizationProvider, Popover, Stack, Typography} from 'views/components';
import {CalendarTodayOutlined} from 'views/icons';
import {Translate, translate} from 'views/module';

import ActualsSection from './sections/actuals/ActualsSection';
import ClientOverviewSection from './sections/client_overview/ClientOverviewSection';
import DataQualitySection from './sections/data_quality/DataQualitySection';
import ExpensesCompletionSection from './sections/expenses_completion/ExpensesCompletionSection';
import FinancialOverviewSection from './sections/financial_overview/FinancialOverviewSection';
import ForecastSection from './sections/forecast/ForecastSection';
import MonthlySummarySection from './sections/monthly_summary/MonthlySummarySection';
import ProjectionsSection from './sections/projections/ProjectionsSection';
import WeeklyBreakdownSection from './sections/weekly_breakdown/WeeklyBreakdownSection';

import backend from 'utils/backend';
import moment from 'moment-timezone';

const ClientMetrics = (props) => {
  const {
    statusProgressWrap: progressWrap,
  } = props;

  const timezone = 'America/Toronto';
  // Snapshots exist up to today in Toronto; future dates can never have one.
  const latestAvailableDate = React.useMemo(() => {
    return moment.tz(timezone).startOf('day');
  }, [timezone]);

  const [comparisonMode, setComparisonMode] = React.useState('WEEKLY');
  const [selectedCurrentDate, setSelectedCurrentDate] = React.useState(latestAvailableDate.format('YYYY-MM-DD'));
  const [datePickerAnchorEl, setDatePickerAnchorEl] = React.useState(null);
  const [snapshotState, setSnapshotState] = React.useState({
    isLoading: true,
    hasLoadedOnce: false,
    errorMessage: '',
    metricsByDate: {},
    fetchedDates: {},
    currentDate: '',
    previousDate: '',
  });

  const comparisonWindow = React.useMemo(() => {
    const currentDateMoment = moment.tz(selectedCurrentDate, 'YYYY-MM-DD', timezone).startOf('day');
    const daysBack = comparisonMode === 'DAILY' ? 1 : 7;

    return {
      currentDate: currentDateMoment.format('YYYY-MM-DD'),
      previousDate: currentDateMoment.clone().subtract(daysBack, 'day').format('YYYY-MM-DD'),
    };
  }, [comparisonMode, selectedCurrentDate, timezone]);

  React.useEffect(() => {
    let isActive = true;

    const loadMetrics = async () => {
      const {currentDate, previousDate} = comparisonWindow;
      const datesToFetch = [currentDate, previousDate].filter((date) => !snapshotState.fetchedDates[date]);

      setSnapshotState((currentState) => ({
        ...currentState,
        isLoading: datesToFetch.length > 0,
        errorMessage: '',
      }));

      // Optimization: do not need to fetch for dates we already fetched
      if (!datesToFetch.length) {
        setSnapshotState((currentState) => ({
          ...currentState,
          currentDate,
          previousDate,
        }));

        return;
      }

      await progressWrap(async () => {
        const [snapshotsResult] = await Promise.allSettled([
          backend.metrics.queryFinancialSnapshots(datesToFetch),
        ]);

        if (!isActive) {
          return;
        }

        if (snapshotsResult.status === 'fulfilled') {
          setSnapshotState((currentState) => {
            const metricsByDate = {
              ...currentState.metricsByDate,
            };

            (snapshotsResult.value?.snapshots || []).forEach((snapshot) => {
              metricsByDate[snapshot.metricDate] = snapshot;
            });

            return {
              ...currentState,
              isLoading: false,
              hasLoadedOnce: true,
              errorMessage: '',
              metricsByDate,
              fetchedDates: {
                ...currentState.fetchedDates,
                ...datesToFetch.reduce((nextFetchedDates, date) => {
                  nextFetchedDates[date] = true;

                  return nextFetchedDates;
                }, {}),
              },
              currentDate,
              previousDate,
            };
          });

          return;
        }

        setSnapshotState((currentState) => ({
          ...currentState,
          isLoading: false,
          errorMessage: snapshotsResult.reason?.message || translate('metrics.ClientMetrics.Errors.LoadError'),
        }));
      });
    };

    loadMetrics();

    return () => {
      isActive = false;
    };
  }, [comparisonWindow, progressWrap]);

  const initialLoading = snapshotState.isLoading && !snapshotState.hasLoadedOnce;
  const currentSnapshot = snapshotState.currentDate ? snapshotState.metricsByDate[snapshotState.currentDate] || null : null;
  const previousSnapshot = snapshotState.previousDate ? snapshotState.metricsByDate[snapshotState.previousDate] || null : null;

  const formatDisplayDate = (date) => {
    return moment.tz(date, 'YYYY-MM-DD', timezone).format('MMM D, YYYY');
  };

  const formattedSelectedDate = formatDisplayDate(selectedCurrentDate);
  const comparisonDatesText = snapshotState.currentDate ?
    translate('metrics.ClientMetrics.ComparisonDates', {
      currentDate: formatDisplayDate(snapshotState.currentDate),
      previousDate: formatDisplayDate(snapshotState.previousDate),
    }) :
    '';
  const generatedAtText = currentSnapshot?.generatedAt ?
    translate('metrics.ClientMetrics.GeneratedAt', {
      timestamp: moment.tz(currentSnapshot.generatedAt, timezone).format('MMM D, YYYY h:mm A'),
    }) :
    '';

  const isSettled = snapshotState.hasLoadedOnce && !snapshotState.isLoading && !snapshotState.errorMessage;
  const showMissingCurrentSnapshot = isSettled && Boolean(snapshotState.currentDate) && !currentSnapshot;
  const showMissingPreviousSnapshot = isSettled && Boolean(currentSnapshot) && Boolean(snapshotState.previousDate) && !previousSnapshot;
  const showSections = !(snapshotState.errorMessage && !snapshotState.hasLoadedOnce);

  return (
    <Box
      sx={{
        minHeight: '100%',
        backgroundColor: '#F9F8F6',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '1160px',
          mx: 'auto',
          px: {xs: 2, md: 4},
          py: {xs: 3, md: 5.5},
        }}
      >
        <Stack spacing={3}>
          <Stack
            direction={{xs: 'column', lg: 'row'}}
            justifyContent='space-between'
            alignItems={{xs: 'flex-start', lg: 'center'}}
            spacing={2}
          >
            <Box sx={{minWidth: 0}}>
              <Typography
                sx={{
                  fontSize: {xs: '24px', md: '28px'},
                  lineHeight: '36px',
                  color: '#1A1916',
                  mb: 1,
                }}
              >
                <Translate expression='metrics.ClientMetrics.Title' />
              </Typography>
              <Typography
                sx={{
                  fontSize: '13px',
                  lineHeight: '17px',
                  color: '#A8A49E',
                }}
              >
                <Translate expression='metrics.ClientMetrics.Subtitle' />
              </Typography>
              {
                comparisonDatesText &&
                <Typography
                  sx={{
                    fontSize: '11px',
                    lineHeight: '15px',
                    color: '#A8A49E',
                    mt: 0.75,
                  }}
                >
                  {comparisonDatesText}
                </Typography>
              }
              {
                generatedAtText &&
                <Typography
                  sx={{
                    fontSize: '11px',
                    lineHeight: '15px',
                    color: '#A8A49E',
                    mt: 0.25,
                  }}
                >
                  {generatedAtText}
                </Typography>
              }
            </Box>
            <Stack direction='row' alignItems='center' spacing={1.5} flexWrap='wrap' useFlexGap>
              <Typography
                sx={{
                  fontSize: '11px',
                  fontWeight: 500,
                  color: '#919191',
                }}
              >
                {
                  comparisonMode === 'DAILY' ?
                    <Translate expression='metrics.ClientMetrics.VsPriorDay' /> :
                    <Translate expression='metrics.ClientMetrics.VsPriorWeek' />
                }
              </Typography>
              <ButtonBase
                onClick={(event) => setDatePickerAnchorEl(event.currentTarget)}
                aria-label={translate('metrics.ClientMetrics.DatePickerLabel')}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.25,
                  py: 0.75,
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #EBEBEB',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                <CalendarTodayOutlined sx={{fontSize: 16, color: '#919191'}} />
                <Typography
                  sx={{
                    fontSize: '11px',
                    fontWeight: 500,
                    color: '#6B6761',
                  }}
                >
                  {formattedSelectedDate}
                </Typography>
              </ButtonBase>
              {
                datePickerAnchorEl &&
                <Popover
                  open={Boolean(datePickerAnchorEl)}
                  anchorEl={datePickerAnchorEl}
                  onClose={() => setDatePickerAnchorEl(null)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  slotProps={{
                    paper: {
                      sx: {
                        mt: 1,
                        borderRadius: '12px',
                        border: '1px solid #E8E4DE',
                        boxShadow: '0px 12px 32px rgba(26, 25, 22, 0.12)',
                      },
                    },
                  }}
                >
                  <Box sx={{p: 1}}>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DateCalendar
                        value={moment.tz(selectedCurrentDate, 'YYYY-MM-DD', timezone)}
                        maxDate={latestAvailableDate}
                        onChange={(nextDate) => {
                          if (!nextDate?.isValid?.()) {
                            return;
                          }

                          setSelectedCurrentDate(nextDate.clone().tz(timezone).format('YYYY-MM-DD'));
                          setDatePickerAnchorEl(null);
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                </Popover>
              }
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  p: 0.5,
                  backgroundColor: '#EEEEEE',
                  borderRadius: '8px',
                }}
              >
                <ButtonBase
                  onClick={() => setComparisonMode('DAILY')}
                  aria-pressed={comparisonMode === 'DAILY'}
                  sx={{
                    px: 1.75,
                    py: 0.75,
                    borderRadius: '6px',
                    backgroundColor: comparisonMode === 'DAILY' ? '#FFFFFF' : 'transparent',
                    boxShadow: comparisonMode === 'DAILY' ? '0px 4px 4px rgba(0, 0, 0, 0.05)' : 'none',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '11px',
                      fontWeight: 500,
                      color: comparisonMode === 'DAILY' ? '#000000' : '#6B6761',
                    }}
                  >
                    <Translate expression='metrics.ClientMetrics.Daily' />
                  </Typography>
                </ButtonBase>
                <ButtonBase
                  onClick={() => setComparisonMode('WEEKLY')}
                  aria-pressed={comparisonMode === 'WEEKLY'}
                  sx={{
                    px: 1.75,
                    py: 0.75,
                    borderRadius: '6px',
                    backgroundColor: comparisonMode === 'WEEKLY' ? '#FFFFFF' : 'transparent',
                    boxShadow: comparisonMode === 'WEEKLY' ? '0px 4px 4px rgba(0, 0, 0, 0.05)' : 'none',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '11px',
                      fontWeight: 500,
                      color: comparisonMode === 'WEEKLY' ? '#000000' : '#6B6761',
                    }}
                  >
                    <Translate expression='metrics.ClientMetrics.Weekly' />
                  </Typography>
                </ButtonBase>
              </Box>
            </Stack>
          </Stack>

          {
            snapshotState.errorMessage &&
            !snapshotState.isLoading &&
            <Alert severity='warning'>
              {snapshotState.errorMessage}
            </Alert>
          }
          {
            showMissingCurrentSnapshot &&
            <Alert severity='info'>
              <Translate
                expression='metrics.ClientMetrics.EmptyStates.MissingSnapshot'
                substitute={{date: formatDisplayDate(snapshotState.currentDate)}}
              />
            </Alert>
          }
          {
            showMissingPreviousSnapshot &&
            <Alert severity='info'>
              <Translate
                expression='metrics.ClientMetrics.EmptyStates.MissingComparisonSnapshot'
                substitute={{date: formatDisplayDate(snapshotState.previousDate)}}
              />
            </Alert>
          }

          {
            showSections &&
            <>
              <ClientOverviewSection
                isLoading={initialLoading}
                currentSnapshot={currentSnapshot}
                previousSnapshot={previousSnapshot}
              />

              <FinancialOverviewSection
                isLoading={initialLoading}
                currentSnapshot={currentSnapshot}
                previousSnapshot={previousSnapshot}
              />

              <ProjectionsSection
                isLoading={initialLoading}
                currentSnapshot={currentSnapshot}
              />

              <MonthlySummarySection
                isLoading={initialLoading}
                currentSnapshot={currentSnapshot}
              />

              <WeeklyBreakdownSection
                isLoading={initialLoading}
                currentSnapshot={currentSnapshot}
              />

              <ForecastSection
                isLoading={initialLoading}
                currentSnapshot={currentSnapshot}
              />

              <ActualsSection
                isLoading={initialLoading}
                currentSnapshot={currentSnapshot}
              />

              <ExpensesCompletionSection
                isLoading={initialLoading}
                currentSnapshot={currentSnapshot}
                previousSnapshot={previousSnapshot}
              />

              <DataQualitySection
                isLoading={initialLoading}
                currentSnapshot={currentSnapshot}
              />
            </>
          }
        </Stack>
      </Box>
    </Box>
  );
};

ClientMetrics.propTypes = {
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
});

export default connect(mapStateToProps, dispatch)(React.memo(ClientMetrics));
